import { Injectable } from '@angular/core';
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { lastValueFrom } from 'rxjs';
import { HttpRequestResponse } from '../models/http-request-response.model';
import { HttpRequestService } from './http-request.service';

@Injectable({ providedIn: 'root' })
export class BiometricAuthService {
  constructor(private httpRequestService: HttpRequestService) { }

  isSupported(): boolean {
    if (
      typeof window === 'undefined' ||
      typeof navigator === 'undefined' ||
      !window.isSecureContext ||
      typeof window.PublicKeyCredential === 'undefined' ||
      !navigator.credentials ||
      typeof navigator.credentials.create !== 'function' ||
      typeof navigator.credentials.get !== 'function' ||
      !window.crypto ||
      typeof window.crypto.getRandomValues !== 'function' ||
      !window.crypto.subtle
    ) {
      return false;
    }

    try {
      return browserSupportsWebAuthn();
    } catch {
      return false;
    }
  }

  async register(): Promise<void> {
    this.ensureSupported();
    const optionsResponse = await lastValueFrom(this.httpRequestService.post('/login/biometric/register/options', {}));
    this.ensureSuccess(optionsResponse);
    const registrationResponse = await startRegistration({ optionsJSON: optionsResponse.data.options });
    const verifyResponse = await lastValueFrom(this.httpRequestService.post('/login/biometric/register/verify', {
      response: JSON.stringify(registrationResponse)
    }));
    this.ensureSuccess(verifyResponse);
  }

  async authenticate(pin: string): Promise<{ access_token: string, refresh_token: string }> {
    this.ensureSupported();
    const optionsResponse = await lastValueFrom(this.httpRequestService.post('/login/biometric/authenticate/options', { pin }));
    this.ensureSuccess(optionsResponse);
    const authenticationResponse = await startAuthentication({ optionsJSON: optionsResponse.data.options });
    const verifyResponse = await lastValueFrom(this.httpRequestService.post('/login/biometric/authenticate/verify', {
      pin,
      response: JSON.stringify(authenticationResponse)
    }));
    this.ensureSuccess(verifyResponse);
    return verifyResponse.data.tokens;
  }

  private ensureSuccess(response: HttpRequestResponse): void {
    if (!HttpRequestResponse.isSuccessResponse(response)) {
      throw new Error(response?.message || 'Operazione biometrica non riuscita');
    }
  }

  private ensureSupported(): void {
    if (!this.isSupported()) {
      throw new Error('Il browser o il dispositivo non supporta l\'accesso biometrico');
    }
  }
}
